import { Observable, Subscription, combineLatest } from 'rxjs';
import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, ChangeDetectorRef, NgZone } from '@angular/core';
import { ILookup } from '../shared/lookup';
import { ICompany } from './company.model';
import { Router, ActivatedRoute } from '@angular/router';
import { AddressBookService } from './address-book.service';
import { AppService } from '../app.service';
import { AuthService } from '../shared/core/index';
import { FormBuilder } from '@angular/forms';
import { IAddressBookSearchData } from './address-book-search-data.model';
import { Location } from '@angular/common';
import { GlobalErrorHandler } from '../shared/core/error-handler';
import { PromptOptions, ModalPromptService } from '../shared/helper-components/modal-prompt.service';

import { NotificationsService, Notification } from '../shared/helper-components/index';

@Component({
    selector: 'address-book',
    standalone: false,
    templateUrl: './address-book.component.html',
    styleUrls: ['./address-book.component.css']
})
export class AddressBookComponent implements OnInit, OnDestroy {

    @ViewChild('maincontainer', { static: false }) public maincontainer: ElementRef;
    @ViewChild('contactCard', { static: false }) public contactCard: ElementRef;

    sortByLookup: ILookup[] = [
        { id: 'Name', name: 'A-Z', type: '' },
        { id: 'ModifiedDate', name: 'Date Modified', type: '' }
    ];
    itemList: ICompany[] = [];
    showSpinner: boolean = false;
    listSearchData: IAddressBookSearchData;
    isLoadingMoreRecords: boolean = false;
    sortOrderTooltip = 'sort desc';
    sortOrderIcon = 'arrow_upward';
    searchMessage: string = '';
    noRecordsFoundMessage: string = 'No items found with the provided filters.';
    showMobileFilters: boolean = false;
    /** True when the last load failed (not an empty success). */
    inErrorState: boolean = false;

    constructor(
        private _router: Router,
        private _route: ActivatedRoute,
        private _addressBookService: AddressBookService,
        private _appService: AppService,
        public _authService: AuthService,
        private location: Location,
        private fb: FormBuilder,
        private _errorHandler: GlobalErrorHandler,
        private _prompt: ModalPromptService,
        private _notificationsService: NotificationsService,
        private cd: ChangeDetectorRef,
        private ngZone: NgZone,
    ) {
    }

    public ngOnInit() {
        this.listSearchData = new IAddressBookSearchData();

        var obsComb = combineLatest(this._route.params, this._route.queryParams,
            (params, qparams) => ({ params, qparams }));
        obsComb.subscribe(
            combine => {
                let companyIdParam = combine.qparams['companyId'];
                this.listSearchData.companyId = companyIdParam;
                this.listSearchData.sortOrder = 'ASC';
                let sortByParam = combine.qparams['sortBy'];
                if (sortByParam)
                    this.listSearchData.sortBy = sortByParam;
                let sortOrder = combine.qparams['sortOrder'];
                if (sortOrder)
                    this.listSearchData.sortOrder = sortOrder;
                let filterString = combine.qparams['filterString'];
                if (filterString)
                    this.listSearchData.filterString = filterString;

                this.filterChanged('onLoad');
            }
        );

        this._appService.scrollBottomReachedEvent.subscribe(() => {
            this.loadMoreRecords();
        });
    }

    ngOnDestroy(): void {
        if (this.promptSubscription && !this.promptSubscription.closed)
            this.promptSubscription.unsubscribe();
    }

    getCompanies(): void {
        this._addressBookService.getCompanies(this.listSearchData).subscribe(
            (data) => {
                this.ngZone.run(() => {
                    this.inErrorState = false;
                    if (!data.resultSet || data.resultSet.length == 0) {
                        this.searchMessage = this.noRecordsFoundMessage;
                    } else {
                        this.searchMessage = '';
                    }
                    this.onListDataRetrieved(data);
                    this.showSpinner = false;
                    this.cd.detectChanges();
                });
            },
            (error: any) => {
                this.ngZone.run(() => {
                    this.itemList = [];
                    this.showSpinner = false;
                    this.inErrorState = error.status != 404;

                    if (error.status == 404) {
                        this.inErrorState = false;
                        this.searchMessage = this.noRecordsFoundMessage;
                    } else {
                        this.searchMessage = this.searchMessage || 'Unexpected error';
                        this._errorHandler.processError(error);
                    }
                    this.cd.detectChanges();
                });
            }
        );
    }

    stopPropagation(e): void {
        e.stopPropagation();
    }

    selectAllItems(e): void {
        if (this.itemList) {
            const checked = e.checked;
            this.itemList.forEach(it => it.isSelected = checked);
        }
    }

    @HostListener('window:scroll')
    public onWindowScroll() {
        if (this.isBottomReached()) {
            this.loadMoreRecords();
        }
    }

    isBottomReached(): Boolean {
        let windowHeight = 'innerHeight' in window ? window.innerHeight
            : document.documentElement.offsetHeight;
        let body = document.body, html = document.documentElement;
        let docHeight = Math.max(body.scrollHeight,
            body.offsetHeight, html.clientHeight,
            html.scrollHeight, html.offsetHeight);
        let windowBottom = windowHeight + window.pageYOffset;
        if (windowBottom >= docHeight) {
            return true;
        }
        return false;
    }

    moreRecords(): boolean {
        if (!this.isLoadingMoreRecords) {
            let nextPageRecordStartIndex = this.listSearchData.pazesize + this.listSearchData.pageStartIndex;
            return this.listSearchData.totalNumberOfRecords > nextPageRecordStartIndex && nextPageRecordStartIndex < 2000;
        }
        return false;
    }

    manualLoad(): void {
        this.loadMoreRecords();
        setTimeout(() => {
            this.maincontainer.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
        });
    }

    loadMoreRecords(): void {
        if (!this.isLoadingMoreRecords) {
            let nextPageRecordStartIndex = this.listSearchData.pazesize + this.listSearchData.pageStartIndex;
            if (this.listSearchData.totalNumberOfRecords > nextPageRecordStartIndex && nextPageRecordStartIndex < 2000) {
                this.isLoadingMoreRecords = true;
                this.listSearchData.pageStartIndex = nextPageRecordStartIndex;
                this._addressBookService.getCompanies(this.listSearchData).subscribe(
                    (data) => {
                        this.ngZone.run(() => {
                            this.isLoadingMoreRecords = false;
                            if (data.resultSet) {
                                this.listSearchData.totalNumberOfRecords = data.totalNumberOfRecords;
                                this.listSearchData.pageStartIndex = data.pageStartIndex;
                                data.resultSet.forEach(it => this.itemList.push(it));
                            }
                            this.cd.detectChanges();
                        });
                    },
                    (error: any) => {
                        this.ngZone.run(() => {
                            this.isLoadingMoreRecords = false;
                            this.itemList = [];
                            if (error.status != 404) {
                                this._errorHandler.processError(error);
                            }
                            this.cd.detectChanges();
                        });
                    }
                );
            }
        }
    }

    onListDataRetrieved(listSearchData: IAddressBookSearchData): void {
        this.itemList = [];
        if (listSearchData.resultSet) {
            listSearchData.resultSet.forEach(item => this.itemList.push(item));
            this.listSearchData.totalNumberOfRecords = listSearchData.totalNumberOfRecords;
            this.listSearchData.pageStartIndex = listSearchData.pageStartIndex;
        }
    }

    filterChanged(filterType: string): void {
        window.scrollTo(0, 0);

        if (filterType == 'sortOrder') {
            if (!this.listSearchData.sortOrder || this.listSearchData.sortOrder == 'ASC') {
                this.listSearchData.sortOrder = 'DESC';
                this.sortOrderIcon = 'arrow_downward';
                this.sortOrderTooltip = 'sort asc';
            } else {
                this.listSearchData.sortOrder = 'ASC';
                this.sortOrderIcon = 'arrow_upward';
                this.sortOrderTooltip = 'sort desc';
            }
        } else if (filterType == 'clear') {
            this.listSearchData.sortBy = this.sortByLookup[0].id;
            this.listSearchData.sortOrder = null;
            this.listSearchData.filterString = null;
        }

        this.listSearchData.pazesize = 15;
        this.listSearchData.pageStartIndex = 0;
        this.listSearchData.totalNumberOfRecords = 0;
        this.getCompanies();
        if (filterType != 'onLoad') {
            this.updateRouteURL();
        }
        this.showMobileFilters = false;
    }

    updateRouteURL(): void {
        let newUrl = '/addressbook';
        if (this.listSearchData.sortBy) {
            newUrl += '?sortBy=' + this.listSearchData.sortBy;
        } else {
            newUrl += '?sortBy=CreatedDate';
        }
        if (this.listSearchData.sortOrder) {
            newUrl += '&sortOrder=' + this.listSearchData.sortOrder;
        }
        if (this.listSearchData.filterString) {
            newUrl += '&filterString=' + this.listSearchData.filterString;
        }
        this.location.replaceState(newUrl);
        this._appService.listViewURL = newUrl;
    }

    anyItemSelected(): boolean {
        return this.itemList && this.itemList.filter(it => it.isSelected).length > 0;
    }

    showCompanyDetail(companyId: string): void {
        this._router.navigate(['addressbook', companyId]);
    }

    deleteCompanies(): void {
        var selectedItems = this.itemList.filter(it => it.isSelected);
        selectedItems.forEach(item =>
            this._addressBookService.deleteCompany(item.id).subscribe(
                (r) => {
                    var index = this.itemList.map(function (c) {
                        return c.id;
                    }).indexOf(item.id);
                    this.itemList.splice(index, 1);
                },
                (error: any) => {
                    this._errorHandler.processError(error);
                }
            )
        );
        this.onDeleteComplete();
    }

    promptOptions: PromptOptions = { header: 'Delete Companies', body: '' };
    promptSubscription: Subscription;

    openModal() {
        this.showMobileFilters = false;
        this.promptOptions.body = 'Are you sure you want to delete the selected Companies?';
        this.promptSubscription = this._prompt.openModal(null, true, this.promptOptions)
            .subscribe(
                answerIsYes => {
                    if (answerIsYes) {
                        this.deleteCompanies();
                        this.showMobileFilters = false;
                    }
                });
        return false;
    }

    onDeleteComplete(): void {
        this._router.navigate(['addressbook']);
    }

    CopyClipboard(e): void {
        let s = e.innerHTML;

        var temp = document.createElement('div');
        temp.innerHTML = e.innerHTML.replace(/\n{2,}/g, '\n');
        var sanitized = temp.textContent || temp.innerText;
        sanitized = sanitized.replace(/\n\s*\n/g, '\n');

        var rx = new RegExp('<i[^>]*>(.*?)</i>', 'gm');
        s = s.replace(rx, '');

        const clipboardItem = new ClipboardItem({
            'text/plain': new Blob([sanitized], { type: 'text/plain' }),
            'text/html': new Blob([s], { type: 'text/html' })
        });

        navigator.clipboard.write([clipboardItem]).then(() => {
            this._notificationsService.add(new Notification('success', 'Contact has been copied to the clipboard.'));
        });
    }
}
